import type { EventsCacheSlots } from "@tento-lona/sheets/platform";
import { EVENTS_FACADE } from "@tento-lona/sheets/platform";
import { CalendarColumn } from "@tento-ui/components/calendar/calendar-column";
import type { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import type { EventItemTimed, Option } from "@tento-lona/sheets";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { DateFragment, Time } from "@tento-chrono";
import { MoreIndicator } from "../../../renderer/timeline-row-event/all-day-events/more-indicator.ts";
import type { CalendarSheetColumn } from "./calendar-sheet-column.ts";
import type { CalendarDaysStrip, SheetNamedChildren } from "./calendar-days-strip.ts";

/**
 * A days strip's "more above / more below" indicators for one day fragment
 * (F11): events of the full day that the strip's window hides, with their
 * click handler and tooltip. Moved out of calendar-days-strip.ts unchanged;
 * props and deps are read through `ctx` when the indicator is clicked or
 * hovered, exactly as the strip's own fields were.
 */

/** What the indicators read from the strip. */
export type OcclusionContext = {
  /** This bind's query cache (shared with the span reads). */
  readonly queryCache: Option<EventsCacheSlots>;
  /** The strip's current props. */
  readonly props: () => CalendarDaysStrip.Props;
  /** The strip's current dependencies. */
  readonly deps: () => CalendarSheetColumn.Dependencies;
};

export function renderOccludedIndicators(
  $frag: CalendarColumn<SheetNamedChildren>,
  windowed: DateFragment.Windowed,
  subcolumns: CalendarSubcolumn[],
  column: ColumnIndex.Key.Resolved.WithSubindex,
  ctx: OcclusionContext,
): void {
  const validHours = windowed.validHours;
  const isFullDay =
    validHours.start.asMs === 0 &&
    (validHours.end.asMs === 0 || validHours.end.asMs === Time.MS_PER_DAY);

  // No occlusion possible if showing full day
  if (isFullDay) {
    $frag.removeNamedChild("moreAbove");
    $frag.removeNamedChild("moreBelow");
    return;
  }

  // Get all events for the full day (without validHours filtering)
  const fullDayRange = windowed.fragment.inner.toUtc();
  // Only compare against validHours, not partialWindow
  // Events outside partialWindow should NOT show as occluded
  const validHoursRange = windowed.applyValidHours().toUtc();

  const occludedAbove: EventItemTimed[] = [];
  const occludedBelow: EventItemTimed[] = [];

  for (const subcolumn of subcolumns) {
    if (!subcolumn.occludedItems) continue;

    const allEvents = subcolumn.occludedItems(fullDayRange, ctx.queryCache);
    for (const e of allEvents) {
      // Check if event is completely before validHours start
      if (e.time.time.end.mse <= validHoursRange.start.mse) {
        occludedAbove.push(e);
      }
      // Check if event is completely after validHours end
      else if (e.time.time.start.mse >= validHoursRange.end.mse) {
        occludedBelow.push(e);
      }
    }
  }

  const rowId = ctx.props().rowId;
  // First span provider that owns a click handler wins. In practice
  // today there's one event-shape subcolumn per fragment, so this is
  // equivalent to "the events subcolumn"; the rule disambiguates the
  // future case of multiple span providers.
  const onShowOccluded =
    subcolumns.find((s) => s.onShowOccluded)?.onShowOccluded ?? null;
  // First subcolumn that owns the tooltip renderer wins. Mirrors the
  // onShowOccluded selection above; the renderer side decides how to
  // present occluded items (today: EventTooltip).
  const renderOccludedTooltip =
    subcolumns.find((s) => s.renderOccludedTooltip)?.renderOccludedTooltip ??
    null;

  // Render above indicator
  if (occludedAbove.length > 0 && !$frag.hasAttribute("partial-top")) {
    const $more = $frag.$namedChildOrElse(
      "moreAbove",
      () => MoreIndicator.make(),
      "unclipped",
    );
    $more.assignStyles({
      "--dot-height": "6px",
      height: "12px",
    });
    $more.toggleAttribute("floating", true);
    $more.toggleAttribute("top", true);
    $more.addPointerEvent({
      onClick: async () => {
        const row = ctx.props().row.findRowWithFacade(EVENTS_FACADE);
        if (!row) return;
        onShowOccluded?.({
          events: occludedAbove,
          side: "above",
          validHours,
          date: windowed.fragment.date,
          tz: ctx.props().date[1],
          row,
        });
      },
    });
    $more.bind(
      occludedAbove,
      ctx.deps().presentation,
      () => {
        const $tt = renderOccludedTooltip?.(occludedAbove);
        if (!$tt) return;
        ctx.deps().tooltip?.showTooltip(
          "timed-events-more-above",
          $tt,
          {
            x: column,
            y: rowId,
          },
          "top",
          {
            offset: {
              x: 0,
              y: 10,
            },
          },
        );
      },
      () => {
        ctx.deps().tooltip?.hideTooltip("timed-events-more-above");
      },
      2,
    );
  } else {
    $frag.removeNamedChild("moreAbove");
  }

  // Render below indicator
  if (occludedBelow.length > 0 && !$frag.hasAttribute("partial-bottom")) {
    const $more = $frag.$namedChildOrElse(
      "moreBelow",
      () => MoreIndicator.make(),
      "unclipped",
    );
    $more.assignStyles({
      "--dot-height": "6px",
      height: "12px",
    });
    $more.toggleAttribute("floating", true);
    $more.toggleAttribute("bottom", true);
    $more.addPointerEvent({
      onClick: async () => {
        const row = ctx.props().row.findRowWithFacade(EVENTS_FACADE);
        if (!row) return;
        onShowOccluded?.({
          events: occludedBelow,
          side: "below",
          validHours,
          date: windowed.fragment.date,
          tz: ctx.props().date[1],
          row,
        });
      },
    });
    $more.bind(
      occludedBelow,
      ctx.deps().presentation,
      () => {
        const $tt = renderOccludedTooltip?.(occludedBelow);
        if (!$tt) return;
        ctx.deps().tooltip?.showTooltip(
          "timed-events-more-below",
          $tt,
          {
            x: column,
            y: rowId,
          },
          "bottom",
          {
            offset: {
              x: 0,
              y: -12,
            },
          },
        );
      },
      () => {
        ctx.deps().tooltip?.hideTooltip("timed-events-more-below");
      },
      2,
    );
  } else {
    $frag.removeNamedChild("moreBelow");
  }
}
